import React from 'react';
import { StyleSheet, TouchableOpacity, type TouchableOpacityProps, View } from 'react-native';

import { theme } from '@garnish/constants';

import { Icon } from '../../../../../Icon';
import { BodyText, LabelText } from '../../../../../Text';
import { NavbarGlassView } from '../NavbarGlassView/NavbarGlassView';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarFloatingLocationButton = (props: Props) => {
  const { eyebrow, onPress, shadow, title, ...restProps } = props;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      {...restProps}
      accessibilityRole="link"
      activeOpacity={0.7}
      onPress={onPress}
      style={styles.outerContainer}
    >
      <NavbarGlassView canHover shadow={shadow} style={styles.container}>
        <View style={styles.contentContainer}>
          <View style={styles.titleContainer}>
            {eyebrow ? (
              <LabelText
                maxFontSizeMultiplier={1.2}
                numberOfLines={1}
                sizeMatch={['10']}
                style={styles.eyebrow}
              >
                {eyebrow}
              </LabelText>
            ) : undefined}

            <BodyText
              maxFontSizeMultiplier={1.2}
              numberOfLines={1}
              sizeMatch={['16']}
              style={eyebrow ? styles.selectedLabel : styles.label}
            >
              {title}
            </BodyText>
          </View>

          <Icon
            name="IconCaretDown"
            width={16}
            height={16}
            color={eyebrow ? theme.colors.KALE : theme.colors.CHARCOAL}
          />
        </View>
      </NavbarGlassView>
    </TouchableOpacity>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    flexShrink: 1,
    maxWidth: 400,
  },
  container: {
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing['2'],
    paddingHorizontal: theme.spacing['6'],
  },
  contentContainer: {
    flexShrink: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
  titleContainer: {
    flexShrink: 1,
    alignItems: 'flex-end',
  },
  eyebrow: {
    color: theme.colors.OPACITY.DARK_KALE.DARK,
    textAlign: 'right',
    textTransform: 'uppercase',
  },
  label: {
    flexShrink: 1,
    color: theme.colors.CHARCOAL,
    textAlign: 'right',
  },
  selectedLabel: {
    flexShrink: 1,
    color: theme.colors.CHARCOAL,
    textAlign: 'right',
    textDecorationLine: 'underline',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  eyebrow?: string;
  shadow?: boolean;
  title: string;
} & Pick<TouchableOpacityProps, 'aria-label' | 'onPress'>;
